import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { UserNotificationsStore } from './user-notification/user-notifications.store';
import { UserNotificationsService } from './user-notification/user-notifications.service';
import { HeaderAction, MenuMode, NavAction } from '../models';
import { ProjectStore, SessionRepository } from '@frontend-site/shared/core';
import { sortBy } from '@frontend-site/shared/utils';

@Injectable()
export class HeaderActionsService {
    private readonly notificationsAction: HeaderAction = {
        id: NavAction.Notification,
        type: 'MENU',
        icon: { name: 'bell--black', size: 'lg' },
        badge: { show: this.userNotificationsStore.showBadge$, color: '#E11731' },
        tooltip: this.userNotificationsService.getUserNotificationsTooltip(),
        automation: 'user-notification',
        order: 0
    };

    private readonly helpAction: HeaderAction = {
        id: NavAction.Help,
        type: 'BUTTON',
        icon: { name: 'question', size: 'lg', iconClass: 'p-DropDown--Help' },
        badge: { show: of(false) },
        tooltip: of('Layouts.Masthead.HELP'),
        automation: 'help-and-support',
        order: 1
    };
    private readonly requestStatusAction: HeaderAction = {
        id: NavAction.RequestStatus,
        type: 'BUTTON',
        icon: { name: 'track-status', size: 'sm' },
        badge: { show: of(false) },
        tooltip: of('Layouts.Masthead.REQUEST_CENTER_TOOLTIP'),
        automation: 'RequestCenter',
        order: 2
    };
    private readonly settingsAction: HeaderAction = {
        id: NavAction.Settings,
        type: 'LINK',
        icon: { name: 'settings', size: 'md' },
        badge: { show: of(false) },
        tooltip: of('Layouts.Masthead.SETTINGS'),
        automation: 'SettingsMenu',
        routePath: '/systemSetup',
        order: 3
    };
    private readonly searchAction: HeaderAction = {
        id: NavAction.Search,
        type: 'MENU',
        icon: { name: 'search', size: 'md' },
        badge: { show: of(false) },
        tooltip: of('Layouts.Masthead.SEARCH'),
        automation: 'p-search-open-button',
        order: 4
    };
    private readonly copyDetailsAction: HeaderAction = {
        id: NavAction.CopyDetails,
        type: 'BUTTON',
        icon: { name: 'copy', size: 'md' },
        order: 10
    };

    constructor(
        private userNotificationsStore: UserNotificationsStore,
        private userNotificationsService: UserNotificationsService,
        private readonly session: SessionRepository,
        private readonly projectStore: ProjectStore
    ) {}

    getActions(mode: MenuMode): Observable<HeaderAction[]> {
        const appConfig = this.session.applicationConfig;
        if (!appConfig) {
            return of([]);
        }

        const actions = this.buildActions(mode, this.projectStore.projectId);

        if (appConfig.hasPermission4InternalTools) {
            actions.push(this.copyDetailsAction);
        }

        return of(actions.sort(sortBy('order')).reverse());
    }

    private buildActions(mode: MenuMode, projectId?: number | null): HeaderAction[] {
        if (mode === MenuMode.SETTINGS) {
            return this.getSettingsActions();
        }

        if (projectId) {
            return this.getInProjectActions();
        }

        const actions = [this.notificationsAction, this.helpAction, this.settingsAction];
        return actions;
    }

    private getSettingsActions(): HeaderAction[] {
        return [this.notificationsAction, this.helpAction];
    }

    private getInProjectActions(): HeaderAction[] {
        return [this.notificationsAction, this.helpAction, this.requestStatusAction, this.settingsAction, this.searchAction];
    }
}
